import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useState } from 'react';
import {
  fetchCancellationPolicies,
  fetchDsos,
  updateDsoBookingFeeRate,
  updateDsoCancellationPolicy,
  updateDsoNetTerms,
  updateDsoPreApproval,
} from '../../../../services/dsos';
import { showErrorMessage, showSuccessMessage } from '../../../../utils';
import {
  filterCancellationPolicies,
  getDefaultCancellationPolicyId,
} from '../../../../utils/CancellationPolicyUtils';

const DSO_CONTROLS_QUERY_KEY = 'DSO_CONTROLS';
const CANCELLATION_POLICIES_QUERY_KEY = 'CANCELLATION_POLICIES';

export const useDsoControls = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const limit = 25;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [DSO_CONTROLS_QUERY_KEY, page, search],
    queryFn: () => fetchDsos({ page, limit, search: search || undefined }),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    initialData: { data: [], total: 0 },
  });

  const { data: cancellationPoliciesRaw = [] } = useQuery({
    queryKey: [CANCELLATION_POLICIES_QUERY_KEY],
    queryFn: fetchCancellationPolicies,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
  });

  const cancellationPolicies = filterCancellationPolicies(cancellationPoliciesRaw);
  const defaultCancellationPolicyId = getDefaultCancellationPolicyId(cancellationPoliciesRaw);

  const readBeMessage = (error) =>
    error?.response?.data?.message || error?.response?.data?.errors?.[0]?.message;

  const invalidateDsoList = () => {
    queryClient.invalidateQueries({ queryKey: [DSO_CONTROLS_QUERY_KEY] });
  };

  const netTermsMutation = useMutation({
    mutationFn: updateDsoNetTerms,
    meta: { skipGlobalErrorToaster: true },
    onSuccess: () => {
      showSuccessMessage({ message: 'Net Terms was updated successfully' });
      invalidateDsoList();
    },
    onError: (error) => {
      showErrorMessage({ message: readBeMessage(error) || 'Failed to update Net Terms.' });
    },
  });

  const bookingFeeMutation = useMutation({
    mutationFn: updateDsoBookingFeeRate,
    meta: { skipGlobalErrorToaster: true },
    onSuccess: () => {
      showSuccessMessage({ message: 'RDH Booking Fee was updated successfully' });
      invalidateDsoList();
    },
    onError: (error) => {
      const beMessage = readBeMessage(error);
      if (beMessage) {
        showErrorMessage({ message: beMessage });
        return;
      }
      if (error?.response?.status === 403) {
        showErrorMessage({
          message: 'You do not have the permissions to change the RDH Booking Fee',
        });
        return;
      }
      showErrorMessage({ message: 'Failed to update RDH Booking Fee.' });
    },
  });

  const cancellationPolicyMutation = useMutation({
    mutationFn: updateDsoCancellationPolicy,
    meta: { skipGlobalErrorToaster: true },
    onSuccess: () => {
      showSuccessMessage({ message: 'Cancellation Policy was updated successfully' });
      invalidateDsoList();
    },
    onError: (error) => {
      showErrorMessage({
        message: readBeMessage(error) || 'Failed to update Cancellation Policy.',
      });
    },
  });

  const preApprovalMutation = useMutation({
    mutationFn: updateDsoPreApproval,
    meta: { skipGlobalErrorToaster: true },
    onSuccess: () => {
      showSuccessMessage({ message: 'Office has been pre-approved successfully' });
      invalidateDsoList();
    },
    onError: (error) => {
      showErrorMessage({ message: readBeMessage(error) || 'Failed to update Pre-Approval.' });
    },
  });

  const updateNetTerms = (id, netTerms) => {
    netTermsMutation.mutate({ id, netTerms });
  };

  const updateBookingFeeRate = (id, bookingFeeRate, options) => {
    bookingFeeMutation.mutate({ id, bookingFeeRate }, options);
  };

  const updateCancellationPolicy = (id, cancellationPolicy) => {
    cancellationPolicyMutation.mutate({ id, cancellationPolicy });
  };

  const updatePreApproval = (id, isPreApproved) => {
    preApprovalMutation.mutate({ id, isPreApproved });
  };

  const totalPages = Math.ceil((data?.total || 0) / limit);

  return {
    dsos: data?.data || [],
    isLoading: isLoading || isFetching,
    page,
    setPage,
    search,
    setSearch,
    totalPages,
    total: data?.total || 0,
    updateNetTerms,
    isUpdatingNetTerms: netTermsMutation.isPending,
    updateBookingFeeRate,
    isUpdatingBookingFeeRate: bookingFeeMutation.isPending,
    updatePreApproval,
    isUpdatingPreApproval: preApprovalMutation.isPending,
    cancellationPolicies,
    defaultCancellationPolicyId,
    updateCancellationPolicy,
    isUpdatingCancellationPolicy: cancellationPolicyMutation.isPending,
  };
};
